import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BlockIcon from "@mui/icons-material/Block";
import { useTranslation } from "react-i18next";
import { useNotification } from "@context/NotificationContext";
import { useAppDispatch, useAppSelector } from "@/store";
import { fetchShareLinks, deactivateShareLink } from "@/store/shareLinksSlice";
import { fetchFiles } from "@/store/filesSlice";
import { ShareLinkDialog } from "@components/ShareLinkDialog";
import { PageContainer } from "@components/PageContainer";
import { TableHeaderCell } from "@components/TableHeaderCell";
import { formatTableDate } from "@/utils/format";
import {
  ShareLinkRowStatus,
  chipStyleForShareLinkStatus,
  resolveShareLinkRowStatus,
} from "@/utils/shareLinkStatus";

const STATUS_LABEL_KEYS: Record<ShareLinkRowStatus, string> = {
  [ShareLinkRowStatus.Active]: "shareLink.statusLabelActive",
  [ShareLinkRowStatus.Inactive]: "shareLink.statusLabelInactive",
  [ShareLinkRowStatus.Expired]: "shareLink.statusLabelExpired",
  [ShareLinkRowStatus.Burned]: "shareLink.statusLabelBurned",
};

export default function SharedLinks() {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const dispatch = useAppDispatch();
  const { shareLinks } = useAppSelector((s) => s.shareLinks);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchShareLinks());
    dispatch(fetchFiles({ limit: 100 }));
  }, [dispatch]);

  const handleCopy = (token: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/s/${token}`);
    notify(t("shareLink.copied"), "success");
  };

  const handleDeactivate = async (id: string) => {
    await dispatch(deactivateShareLink(id));
    notify(t("shareLink.deactivated"), "success");
  };

  return (
    <PageContainer>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
          }}
        >
          {t("shareLink.title")}
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          {t("shareLink.create")}
        </Button>
      </Box>
      <Card>
        {shareLinks.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              {t("shareLink.noLinks")}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t("shareLink.colToken")}</TableHeaderCell>
                  <TableHeaderCell>{t("shareLink.colFiles")}</TableHeaderCell>
                  <TableHeaderCell>{t("shareLink.colCreated")}</TableHeaderCell>
                  <TableHeaderCell>{t("shareLink.colExpires")}</TableHeaderCell>
                  <TableHeaderCell>{t("shareLink.colDownloads")}</TableHeaderCell>
                  <TableHeaderCell>{t("shareLink.colStatus")}</TableHeaderCell>
                  <TableHeaderCell align="right">{t("shareLink.colActions")}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shareLinks.map((link) => {
                  const status = resolveShareLinkRowStatus(link);
                  const styles = chipStyleForShareLinkStatus(status);
                  return (
                    <TableRow key={link.id} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontSize: 13,
                          }}
                        >
                          {link.token.slice(0, 12)}...
                        </Typography>
                      </TableCell>
                      <TableCell>{link.shareLinkFiles?.length || 0}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          {formatTableDate(link.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          {formatTableDate(link.expiresAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {link.downloadCount}/{link.maxDownloads}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={t(STATUS_LABEL_KEYS[status])}
                          size="small"
                          sx={{
                            bgcolor: styles.bg,
                            color: styles.color,
                            fontWeight: 600,
                            fontSize: 12,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {link.isActive && new Date(link.expiresAt) > new Date() && (
                          <>
                            <Tooltip title={t("shareLink.copyLink")}>
                              <IconButton size="small" onClick={() => handleCopy(link.token)}>
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("shareLink.deactivateTooltip")}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeactivate(link.id)}
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
      <ShareLinkDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </PageContainer>
  );
}
